import { TrendingUp, TrendingDown, Minus } from "lucide-react";

// Semantica del test/protocollo. Default sicuro: UNDEFINED → nessuna interpretazione.
export const SEMANTICS = [
  ["HIGHER_IS_BETTER", "Più alto = migliore"],
  ["LOWER_IS_BETTER", "Più basso = migliore"],
  ["TARGET_RANGE", "Intervallo target"],
  ["CLOSER_TO_ZERO", "Più vicino a zero = migliore"],
  ["DESCRIPTIVE_ONLY", "Solo descrittivo"],
  ["UNDEFINED", "Da definire"],
];
export const SEMANTICS_LABEL = Object.fromEntries(SEMANTICS);
export const SEMANTICS_SHORT = { HIGHER_IS_BETTER: "↑ meglio", LOWER_IS_BETTER: "↓ meglio", TARGET_RANGE: "target", CLOSER_TO_ZERO: "→0 meglio", DESCRIPTIVE_ONLY: "descrittivo", UNDEFINED: "da definire" };

export const semanticsOf = (test) => (SEMANTICS_LABEL[test?.semantics] ? test.semantics : "UNDEFINED");
export const hasDirection = (test) => ["HIGHER_IS_BETTER", "LOWER_IS_BETTER", "CLOSER_TO_ZERO", "TARGET_RANGE"].includes(semanticsOf(test));

const rangeDist = (v, rule) => {
  const lo = parseFloat(rule?.min), hi = parseFloat(rule?.max);
  if (isNaN(lo) || isNaN(hi)) return null;
  return v >= lo && v <= hi ? 0 : Math.min(Math.abs(v - lo), Math.abs(v - hi));
};

// best solo quando la semantica lo consente; null altrimenti
export function bestOf(values, test) {
  if (!values?.length) return null;
  switch (semanticsOf(test)) {
    case "HIGHER_IS_BETTER": return Math.max(...values);
    case "LOWER_IS_BETTER": return Math.min(...values);
    case "CLOSER_TO_ZERO": return values.reduce((a, b) => (Math.abs(b) < Math.abs(a) ? b : a));
    case "TARGET_RANGE": { if (rangeDist(values[0], test.semantics_rule) == null) return null; return values.reduce((a, b) => (rangeDist(b, test.semantics_rule) < rangeDist(a, test.semantics_rule) ? b : a)); }
    default: return null;
  }
}

// Valore rappresentativo dei tentativi (result_mode). Con semantica indefinita e mode "best" → ultimo tentativo (nessun best automatico).
export function computeResult(attempts, test) {
  if (!attempts?.length) return null;
  const mode = test?.result_mode || "best";
  if (mode === "sum") return attempts.reduce((s, v) => s + v, 0);
  if (mode === "average") return attempts.reduce((s, v) => s + v, 0) / attempts.length;
  if (mode === "single") return attempts[attempts.length - 1];
  const b = bestOf(attempts, test);
  return b != null ? b : attempts[attempts.length - 1];
}
export const resultLabel = (test) => { const m = test?.result_mode || "best"; if (m === "average") return "Media (risultato)"; if (m === "sum") return "Somma"; if (m === "single") return "Valore"; return bestOf([1], test) != null ? "Best" : "Valore (ultimo tent.)"; };

// true/false solo con semantica definita; null = nessuna interpretazione
export function isImprovement(current, previous, test) {
  if (current == null || previous == null || current === previous) return null;
  const rule = test?.semantics_rule;
  switch (semanticsOf(test)) {
    case "HIGHER_IS_BETTER": return current > previous;
    case "LOWER_IS_BETTER": return current < previous;
    case "CLOSER_TO_ZERO": return Math.abs(current) < Math.abs(previous);
    case "TARGET_RANGE": { const a = rangeDist(current, rule), b = rangeDist(previous, rule); return a == null || a === b ? null : a < b; }
    default: return null;
  }
}

// Variazione matematica (sempre) + interpretazione (solo se semantica definita)
export function variation(current, previous, test) {
  if (previous == null || current == null) return null;
  const delta = current - previous;
  const pct = previous !== 0 ? ((delta / Math.abs(previous)) * 100).toFixed(1) : null;
  const improved = Math.abs(delta) < 1e-9 ? null : isImprovement(current, previous, test);
  const neutral = improved == null;
  return {
    delta, pct, improved,
    icon: Math.abs(delta) < 1e-9 ? Minus : neutral ? null : improved ? TrendingUp : TrendingDown,
    color: neutral ? "text-zinc-500" : improved ? "text-emerald-600" : "text-red-600",
    label: Math.abs(delta) < 1e-9 ? "stabile" : neutral ? "variazione" : improved ? "miglioramento" : "peggioramento",
  };
}
